import { InfoSeller } from '@/organisms';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getAllRoutes, getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  pageAgeGate,
  pageHeaderFooter,
  pageTheme,
  seo,
  sliderHero,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import {
  getCathAllSegments,
  getPathsFromIndex,
  getRedirect,
  isNumber,
} from '@/lib/utils';

import type { GetStaticPaths, GetStaticPropsContext } from 'next';
import type { NextPageWithLayout } from 'pages/_app';

const Brand: NextPageWithLayout = ({
  pageProps: { hero, flexibleContent, title, content, pageName },
}: any) => (
  <>
    <div data-pagename={pageName} />
    <HeroSlider hero={hero} />
    <FlexibleContent flexible={flexibleContent?.flexible} type="Marca" />
    {content && (
      <InfoSeller
        title={`Sobre ${title}`}
        description={content}
        backgroundColor="#F8F7F4"
      />
    )}
  </>
);

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string[] }>,
) {
  const { hasRedirect, redirect } = await getRedirect(ctx.params?.slug);

  if (hasRedirect && process.env.NEXT_REDIRECTS_SERVER_ISR_ENABLED === 'true')
    return { redirect };

  // We don't allow the params length to be more than 2.
  if (ctx.params?.slug && ctx.params.slug.length > 2) {
    return {
      notFound: true,
    };
  }

  // We check the second param is a number.
  if (ctx.params!.slug.length === 2 && !isNumber(ctx.params!.slug[1])) {
    return {
      notFound: true,
    };
  }

  const content = `
    marca(id: $slug, idType: SLUG) {
      ${seo}
      ${pageTheme}
      ${pageAgeGate}
      ${pageHeaderFooter}
      title
      content
      hero:${sliderHero('Marca')}
      flexibleContent: ${FlexibleContentBlocks('Marca')}
    }
  `;
  const Query = PageQuery(content, '', 'Marca');

  return await pagePropsServerTransformer({
    staticProps: await getInitialProps(Query, { slug: ctx.params!.slug[0] }),
    type: 'Marca',
    path: routes.brand(ctx.params!.slug.join('/')),
  });
}

export const getStaticPaths = (async () => {
  const paths = (
    process.env.FULL_DEPLOY
      ? await getAllRoutes({ type: 'marcas' })
      : await getPathsFromIndex('brands')
  ).filter((path) => path.replace('/marcas', '') !== '/huebox');

  return {
    paths: getCathAllSegments({
      paths: paths,
      segmentName: 'slug',
      base: 'marcas',
      catchAll: true,
    }),
    fallback: 'blocking',
  };
}) satisfies GetStaticPaths;

Brand.getLayout = getBaseLayout;

export default Brand;
